import { useEffect, useState } from "react";
import axios from "axios";
import { Play, Radio, ArrowRight } from "lucide-react";
import { SOCIAL_LINKS } from "@/components/SocialIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function YouTubeFeed() {
  const [data, setData] = useState({ videos: [], is_live: false, live_embed_url: null, channel_url: SOCIAL_LINKS.youtube });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/youtube/recent?limit=6`);
        if (!cancelled) setData((prev) => ({ ...prev, ...res.data }));
      } catch (err) {
        // graceful degradation — UI falls back to the channel CTA card
        if (typeof console !== "undefined") console.warn("YouTube feed unavailable:", err?.message || err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const channelUrl = data.channel_url || SOCIAL_LINKS.youtube;
  const hasVideos = data.videos && data.videos.length > 0;

  return (
    <section
      data-testid="youtube-section"
      id="youtube"
      className="py-24 md:py-32 bg-surface relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="overline mb-5 inline-flex items-center gap-2">
              <Play size={12} strokeWidth={1.6} /> YouTube
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Watch Seven Pillars on <em className="italic text-terracotta">YouTube.</em>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Longer-form conversations on therapy, supervision, and the everyday life skills that quietly change everything.
            </p>
          </div>
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-channel-cta"
            className="inline-flex items-center gap-2 bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-7 py-3.5 font-medium transition-colors w-fit"
          >
            Subscribe on YouTube <ArrowRight size={16} />
          </a>
        </div>

        {data.is_live && data.live_embed_url && (
          <div
            data-testid="youtube-live-banner"
            className="mb-10 rounded-2xl overflow-hidden border border-terracotta/40 bg-forest text-bone shadow-xl"
          >
            <div className="flex items-center gap-3 px-6 py-4 border-b border-bone/15 bg-terracotta">
              <Radio size={18} strokeWidth={1.8} className="animate-pulse" />
              <p className="overline text-bone tracking-[0.25em]">Live now</p>
            </div>
            <div className="relative aspect-video bg-black">
              <iframe
                src={`${data.live_embed_url}&autoplay=1&mute=1`}
                title="Seven Pillars — Live"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div
            data-testid="youtube-loading"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            aria-hidden="true"
          >
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-sage/20 bg-bone">
                <div className="aspect-video bg-sage/15 animate-pulse" />
                <div className="p-5">
                  <div className="h-3 w-2/3 bg-sage/20 animate-pulse rounded" />
                  <div className="mt-3 h-3 w-1/3 bg-sage/15 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : hasVideos ? (
          <div
            data-testid="youtube-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {data.videos.map((v) => (
              <a
                key={v.id}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`youtube-video-${v.id}`}
                className="group rounded-2xl overflow-hidden border border-sage/25 bg-bone hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-video overflow-hidden bg-black/5">
                  <img
                    src={v.thumbnail}
                    alt={v.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-forest/0 group-hover:bg-forest/15 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="h-14 w-14 rounded-full bg-bone/90 group-hover:bg-terracotta group-hover:text-bone flex items-center justify-center text-forest transition-colors shadow-lg">
                      <Play size={22} strokeWidth={2} fill="currentColor" />
                    </span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display text-lg text-forest font-medium leading-snug group-hover:text-terracotta transition-colors line-clamp-2">
                    {v.title}
                  </h3>
                  {v.published_at && (
                    <p className="mt-auto pt-3 text-xs text-ink/55 uppercase tracking-wider">
                      {formatDate(v.published_at)}
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div
            data-testid="youtube-empty-card"
            className="rounded-2xl border border-sage/25 bg-bone p-10 md:p-14 text-center"
          >
            <p className="font-display text-2xl md:text-3xl text-forest leading-snug max-w-xl mx-auto">
              New videos and live conversations coming soon — subscribe to be notified.
            </p>
            <a
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-empty-cta"
              className="mt-7 inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-3.5 font-medium transition-colors"
            >
              Visit Channel <ArrowRight size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
